import React, { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Container } from "@mui/material";

import {
  Button,
  Divider,
  Modal,
  Popover,
  Select,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconChevronLeft, IconReload } from "@tabler/icons-react";

import dayjs from "dayjs";

import DisplayAlert from "../../components/DisplayAlert";
import MegaLoader from "../../components/MegaLoader";
import TabularData from "../../components/TabularData";
import VehicleForm from "../../components/VehicleForm";

import { Endpoints } from "../../lib/Endpoints";
import { NonPaginatedResponse } from "../../lib/Responses";
import { SaleRequestProps, VinAPIResponse } from "../../lib/Types";
import { PerformRequest, usePerformRequest } from "../../lib/usePerformRequest";

import { Colors } from "../../lib/Data";

import { Carousel } from "@mantine/carousel";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import "./styles.scss";
import { DefaultErrorNotification, DefaultSuccessNotification } from "../../lib/Methods";
import AnimateOnLoad from "../../components/AnimateOnLoad";

const SingleSaleRequest: React.FC = () => {
  const { requestID } = useParams();
  const navigate = useNavigate();
  const {
    data: r,
    isLoading: isLoadingRequest,
    reloadData: reloadRequest,
  } = usePerformRequest<NonPaginatedResponse<SaleRequestProps>>({
    method: "GET",
    route: Endpoints.GetSingleSalesRequest(requestID as string),
  });
  const request = r && r.data && r.data.id ? r.data : undefined;
  const {
    data: rVin,
    isLoading: isLoadingVinDetails,
    reloadData: reloadVinDetails,
  } = usePerformRequest<VinAPIResponse>({
    method: "GET",
    route: Endpoints.GetVehicleDetailsWithVIN(request?.vin as string),
  });

  useEffect(() => {
    if (r && r.data && r.data.id) {
      reloadVinDetails();
    }
  }, [r]);

  const [isVehicleModalOpen, { open: OpenVehicleModal, close: CloseVehicleModal }] =
    useDisclosure(false);

  const [isImagesModalOpen, { open: OpenImagesModal, close: CloseImagesModal }] =
    useDisclosure(false);
  const [isResponseModalOpen, { open: OpenResponseModal, close: CloseResponseModal }] =
    useDisclosure(false);

  const emailForm = useForm({
    initialValues: {
      response: "",
    },
    validate: {
      response: (value) => (value && value.length > 1 ? null : "Select an email type"),
    },
  });

  const [isResponseOpened, setResponseOpened] = useState<boolean>(false);
  const [isResponseSending, setResponseSending] = useState<boolean>(false);

  const SendEmailResponse = async () => {
    const { hasErrors } = emailForm.validate();
    if (!hasErrors) {
      const { response } = emailForm.values;
      setResponseSending(true);
      const r = await PerformRequest({
        method: "POST",
        route: Endpoints.GetSendEmailResponse(requestID as string),
        data: {
          response,
        },
        callback: () => {
          setResponseSending(false);
        },
      });
      setResponseSending(false);
      if (r) {
        if (r.status === 500) {
          DefaultErrorNotification("Could not send email");
        }
        if (r.status === 200) {
          DefaultSuccessNotification("Email has been sent to client");
          reloadRequest();
          reloadVinDetails();
          setResponseOpened(false);
          CloseResponseModal();
        }
      } else {
        DefaultErrorNotification("An error occurred while sending mail");
      }
    }
  };
  return (
    <AnimateOnLoad>
      <Container maxWidth="lg">
        {isLoadingRequest ? (
          <MegaLoader />
        ) : (
          <>
            {request && request.id ? (
              <div className="single-sale-request-container">
                <Modal
                  centered
                  opened={isImagesModalOpen}
                  onClose={CloseImagesModal}
                  title="Vehicle Images"
                  size="xl"
                  className="vehicle-images"
                >
                  <Carousel withIndicators height={400}>
                    {request.images.map((image, index) => {
                      return (
                        <Carousel.Slide key={index}>
                          <div
                            className="image"
                            style={{
                              backgroundImage: `url(${image})`,
                            }}
                          ></div>
                        </Carousel.Slide>
                      );
                    })}
                  </Carousel>
                </Modal>
                <Text className="component-header">
                  {request.year} {request.manufacturer} {request.model}{" "}
                </Text>
                <TabularData
                  headers={[
                    "Email",
                    "Name",
                    "Phone",
                    "Manufacturer",
                    "Model",
                    "Year",
                    "Driveable",
                    "VIN",
                    "Request Date",
                  ]}
                >
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td key={request.email}>{request.email}</Table.Td>
                      <Table.Td key={request.fullname}>{request.fullname}</Table.Td>
                      <Table.Td key={request.phone}>{request.phone}</Table.Td>
                      <Table.Td key={request.manufacturer}>
                        {request.manufacturer}
                      </Table.Td>
                      <Table.Td key={request.model}>{request.model}</Table.Td>
                      <Table.Td key={request.year}>{request.year}</Table.Td>
                      <Table.Td
                        fw={600}
                        key={"driveable"}
                        c={request.driveable ? Colors.GreenPrimary : Colors.RedPrimary}
                      >
                        {request.driveable ? "Yes" : "No"}
                      </Table.Td>
                      <Table.Td key={request.vin} c={Colors.BluePrimary}>
                        {request.vin}
                      </Table.Td>
                      <Table.Td key={request.createdAt}>
                        {dayjs(request.createdAt).format("ddd MMM DD YYYY")}
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </TabularData>
                <div className="flex-row align-center">
                  <Button
                    mt={25}
                    variant="outline"
                    onClick={OpenImagesModal}
                    radius="50px"
                    w={120}
                  >
                    Show Images
                  </Button>
                  <Popover
                    opened={isResponseOpened}
                    // onChange={setResponseOpened}
                    position="bottom"
                    withArrow
                    shadow="md"
                  >
                    <Popover.Target>
                      <Tooltip
                        label={
                          request && request.response && request.response.length > 0
                            ? "You have already sent an email to the client"
                            : "Send reject/accept email to client"
                        }
                      >
                        <Button
                          loading={isResponseSending}
                          disabled={
                            request && request.response && request.response.length > 0
                          }
                          onClick={() => setResponseOpened((o) => !o)}
                          ml={15}
                          mt={25}
                          radius="50px"
                          w={120}
                        >
                          Respond
                        </Button>
                      </Tooltip>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Select
                        label="Response"
                        placeholder="Select email response type"
                        w={220}
                        mb={15}
                        allowDeselect={false}
                        {...emailForm.getInputProps("response")}
                        data={[
                          { label: "Accepted", value: "accepted" },
                          { label: "Rejected", value: "rejected" },
                        ]}
                        disabled={isResponseSending}
                      />
                      <div className="flex-row align-center width-100 justify-between">
                        <Button
                          disabled={isResponseSending}
                          radius="50px"
                          w="49%"
                          onClick={() => {
                            const { hasErrors } = emailForm.validate();
                            if (hasErrors) {
                              DefaultErrorNotification("Please select a response");
                            } else {
                              OpenResponseModal();
                              setResponseOpened(false);
                            }
                          }}
                        >
                          Continue
                        </Button>
                        <Button
                          color={Colors.RedPrimary}
                          variant="outline"
                          ml={15}
                          radius="50px"
                          onClick={() => setResponseOpened(false)}
                          w="49%"
                        >
                          Cancel
                        </Button>
                      </div>
                    </Popover.Dropdown>
                  </Popover>
                </div>
                <Modal
                  centered
                  opened={isResponseModalOpen}
                  onClose={CloseResponseModal}
                  title="Confirm Response"
                  size="md"
                >
                  <Text>Are you sure you want to send this response email</Text>
                  <Text mb={15} c={Colors.RedPrimary}>
                    This action cannot be undone!
                  </Text>
                  <div className="flex-row align-center width-100 justify-between">
                    <Button
                      radius="50px"
                      w="49%"
                      loading={isResponseSending}
                      onClick={SendEmailResponse}
                    >
                      Send Email
                    </Button>
                    <Button
                      color={Colors.RedPrimary}
                      variant="outline"
                      ml={15}
                      radius="50px"
                      onClick={CloseResponseModal}
                      w="49%"
                    >
                      Cancel
                    </Button>
                  </div>
                </Modal>
                <Divider mt={30} mb={20} />
                <div className="flex-row align-center">
                  <Text mr={15} className="component-header">
                    VIN Details
                  </Text>
                  <Button
                    disabled={isLoadingVinDetails}
                    radius="50px"
                    color={Colors.GreenPrimary}
                    onClick={() => {
                      OpenVehicleModal();
                    }}
                  >
                    Add to Inventory
                  </Button>
                </div>
                <br />

                <div className="vin-details">
                  {isLoadingVinDetails ? (
                    <MegaLoader />
                  ) : (
                    <>
                      {rVin && rVin.Count && rVin.Results.length > 0 ? (
                        <>
                          {rVin.Results.filter(
                            (res) => res.Value && res.Value.length > 0
                          ).map((item) => {
                            return (
                              <div className="flex-row item align-center">
                                <Text className="label">{item.Variable}</Text>
                                <Text className="value">{item.Value}</Text>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <DisplayAlert
                          title="Could not get details"
                          children={
                            <div className="flex-col">
                              <Text>
                                There was an error communicating with the VIN API!
                              </Text>
                              <Button
                                color={Colors.BluePrimary}
                                leftSection={<IconReload size={17} />}
                                w={140}
                                onClick={reloadVinDetails}
                              >
                                Try Again
                              </Button>
                            </div>
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <DisplayAlert
                title="No Requests Found"
                children={
                  <div className="flex-col">
                    <Text>No existing record matches request!</Text>
                    <Button
                      color={Colors.BluePrimary}
                      leftSection={<IconChevronLeft size={17} />}
                      w={180}
                      onClick={() => {
                        navigate("/dashboard/requests");
                      }}
                    >
                      Back to requests
                    </Button>
                  </div>
                }
              />
            )}
          </>
        )}
        {request && (
          <VehicleForm
            action="add"
            isFormOpen={isVehicleModalOpen}
            CloseForm={CloseVehicleModal}
            OpenForm={OpenVehicleModal}
            requestID={request?.id}
            vehicle={{
              id: request?.id,
              previousOwner: {
                email: request.email,
                phone: request.phone,
                name: request.fullname,
              },
              manufacturer: request.manufacturer,
              images: request.images,
              createdAt: request.createdAt,
              model: request.model,
              year: request.year,
              color: request.color,
              odometer: request.odometer,
              amount: 0,
              purchaseDate: Date.now(),
              notes: "Vehicle received!",
              driveable: request.driveable,
              vin: request.vin,
            }}
          />
        )}
      </Container>
    </AnimateOnLoad>
  );
};

export default SingleSaleRequest;
